"use client";
import React, { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import Header from "../components/Header";
import { ATLETA_POR_ID, ATUALIZAR_ATLETA, AtletaData, AtletaVars } from "../services/atletaService";
import { useAuth, dispatchSetUser } from "../context/AuthContext";
import { formatCpf } from "../utils/cpf";
import { LISTAR_ESTADOS, LISTAR_CIDADES, Estado, Cidade } from "../services/localizacaoService";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import UpdateAvatarModal from "../components/UpdateAvatarModal";
import { atualizarImagemUsuario, ATUALIZAR_USUARIO } from "../services/userService";

const AccountInfo: React.FC = () => {
    const auth = useAuth();
    const user = (auth?.user as any) || (typeof window !== "undefined" ? JSON.parse(localStorage.getItem("user") || "null") : null);
    const atletaId = user?.atleta?.id ?? "";

    const { data } = useQuery<AtletaData, AtletaVars>(ATLETA_POR_ID, {
        variables: { id: atletaId },
        skip: !atletaId,
        fetchPolicy: 'network-only',
    });

    // Estado dos campos do formulário
    const [nome, setNome] = useState("");
    const [email, setEmail] = useState("");
    const [estado, setEstado] = useState<Estado | null>(null);
    const [cidade, setCidade] = useState<Cidade | null>(null);
    const [dataNascimento, setDataNascimento] = useState("");
    const [telefone, setTelefone] = useState("");
    const [cpf, setCpf] = useState("");
    const [genero, setGenero] = useState("");
    const [tamanhoCamiseta, setTamanhoCamiseta] = useState("");
    const [tamanhoSapato, setTamanhoSapato] = useState<number | "">("");
    const [message, setMessage] = useState("");
    const [erro, setErro] = useState("");
    const [sobre, setSobre] = useState("");
    const [username, setUsername] = useState(user?.username || "");
    const [avatarUrl, setAvatarUrl] = useState<string | null>(user?.imagemUrl || null);
    const [showAvatarModal, setShowAvatarModal] = useState(false);

    const { data: estadosData } = useQuery<{ estados: Estado[] }>(LISTAR_ESTADOS);
    const { data: cidadesData } = useQuery<{ cidades: Cidade[] }>(LISTAR_CIDADES);

    // Atualiza campos quando carrega atleta
    useEffect(() => {
        if (data?.atletaPorId) {
            setNome(data.atletaPorId.usuario.nome || "");
            setEmail(data.atletaPorId.usuario.email || "");
            setUsername(data.atletaPorId.usuario.username || "");
            setAvatarUrl(data.atletaPorId.usuario.imagemUrl || null);
            setCidade(
                data.atletaPorId.cidade
                    ? {
                        id: data.atletaPorId.cidade.id,
                        nome: data.atletaPorId.cidade.nome,
                        estado: data.atletaPorId.cidade.estado,
                        uf: data.atletaPorId.cidade.uf,
                    }
                    : null
            );
            setDataNascimento(data.atletaPorId.dataNascimento || "");
            setTelefone(data.atletaPorId.telefone || "");
            setCpf((data.atletaPorId.cpf || "").replace(/[^0-9]/g, ""));
            setGenero(data.atletaPorId.genero || "");
            setTamanhoCamiseta(data.atletaPorId.tamanhoCamiseta || "");
            setTamanhoSapato(data.atletaPorId.tamanhoSapato ?? "");
            setEstado(
                data.atletaPorId.cidade?.estado
                    ? {
                          id: data.atletaPorId.cidade.estado.id,
                          nome: data.atletaPorId.cidade.estado.nome,
                          uf: data.atletaPorId.cidade.estado.uf,
                      }
                    : null
            );
            setSobre(data.atletaPorId.sobre || "");
        }
    }, [data]);

    const [atualizarAtleta, { loading: saving }] = useMutation(ATUALIZAR_ATLETA);
    const [atualizarUsuario, { loading: savingUser }] = useMutation(ATUALIZAR_USUARIO);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!atletaId) return;
        setMessage("");
        setErro("");
        try {
            await atualizarAtleta({
                variables: {
                    id: atletaId,
                    cidadeId: cidade?.id,
                    dataNascimento,
                    email,
                    sobre,
                    telefone,
                    genero,
                    tamanhoCamiseta: tamanhoCamiseta || null,
                    tamanhoSapato: tamanhoSapato === "" ? undefined : Number(tamanhoSapato),
                },
            });
            await atualizarUsuario({
                variables: {
                    id: user.id,
                    nome,
                    email,
                    username,
                },
            });
            setMessage("Dados atualizados com sucesso.");
        } catch (err) {
            setErro("Erro ao atualizar dados.");
            console.error("Erro ao atualizar atleta:", err);
        }
    };

    const handleSaveAvatar = async (file: File) => {
        if (!user?.id) return;
        try {
            const url = await atualizarImagemUsuario(user.id, file);
            setAvatarUrl(url);
            const updated = { ...user, imagemUrl: url };
            localStorage.setItem("user", JSON.stringify(updated));
            dispatchSetUser(updated);
        } catch (err) {
            console.error("Erro ao atualizar imagem:", err);
        }
    };

    if (!user || !atletaId) {
        return (
            <div className="text-center mt-5">
                <h2>Você não está logado ou não possui um atleta associado.</h2>
                <Link to="/login" className="btn btn-primary mt-3">
                    Fazer Login
                </Link>
            </div>
        );
    }

    return (
        <Fragment>
            <Header />

            <div className="main-content theme-dark-bg right-chat-active">
                <div className="middle-sidebar-bottom">
                    <div className="middle-sidebar-left">
                        <div className="middle-wrap">
                            <div className="card w-100 border-0 bg-white shadow-xs p-0 mb-4">
                                <div className="card-body p-4 w-100 bg-current border-0 d-flex rounded-3">
                                    <Link to="/defaultsettings" className="d-inline-block mt-2">
                                        <i className="ti-arrow-left font-sm text-white"></i>
                                    </Link>
                                    <h4 className="font-xs text-white fw-600 ms-4 mb-0 mt-2">
                                        Detalhes da Conta
                                    </h4>
                                </div>
                                <div className="card-body p-lg-5 p-4 w-100 border-0 ">
                                    <div className="row justify-content-center">
                                        <div className="col-lg-4 text-center">
                                            <figure className="avatar ms-auto me-auto mb-0 mt-2 w100">
                                                <div
                                                    id="avatar-container"
                                                    onClick={() => setShowAvatarModal(true)}
                                                    style={{ position: "relative", display: "inline-block", cursor: "pointer" }}
                                                    className="avatar-hover-container"
                                                >
                                                    <img
                                                        id="avatar"
                                                        src={avatarUrl || "assets/images/user.png"}
                                                        alt="avatar"
                                                        className="shadow-sm rounded-3 w-100"
                                                        style={{ display: "block" }}
                                                    />
                                                    <div
                                                        id="camera-overlay"
                                                        className="camera-overlay"
                                                        style={{
                                                            position: "absolute",
                                                            top: "50%",
                                                            left: "50%",
                                                            transform: "translate(-50%, -50%)",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            width: "100%",
                                                            height: "100%",
                                                            background: "rgba(0,0,0,0.1)",
                                                            borderRadius: "inherit",
                                                            pointerEvents: "none",
                                                            opacity: 0,
                                                            transition: "opacity 0.2s"
                                                        }}
                                                    >
                                                        <i className="bi bi-camera fs-1 fw-bold" style={{ color: "#fff" }}></i>
                                                    </div>
                                                    <style>
                                                        {`
                                                            .avatar-hover-container:hover .camera-overlay {
                                                                opacity: 1 !important;
                                                                pointer-events: auto;
                                                            }
                                                        `}
                                                    </style>
                                                </div>
                                            </figure>
                                            <h2 className="fw-700 font-sm text-grey-900 mt-3">
                                                {nome}
                                            </h2>
                                            <h4 className="text-grey-500 fw-500 mb-3 font-xsss mb-4">
                                                {cidade?.nome} - {estado?.uf}
                                            </h4>
                                        </div>
                                    </div>

                                    <form onSubmit={handleSubmit}>
                                        {message && (
                                            <div className="alert alert-success" role="alert">
                                                {message}
                                            </div>
                                        )}
                                        {erro && (
                                            <div className="alert alert-danger" role="alert">
                                                {erro}
                                            </div>
                                        )}
                                        <div className="row">
                                            <div className="col-lg-12 mb-3">
                                                <div className="form-group">
                                                    <label className="mont-font fw-600 font-xsss mb-2">
                                                        Nome Completo
                                                    </label>
                                                    <input
                                                        id="usuario-nome"
                                                        type="text"
                                                        className="form-control"
                                                        value={nome}
                                                        onChange={(e) => setNome(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-lg-12 mb-3">
                                                <div className="form-group">
                                                    <label className="mont-font fw-600 font-xsss mb-2">
                                                        Nome de usuário
                                                    </label>
                                                    <input
                                                        id="usuario-username"
                                                        type="text"
                                                        className="form-control"
                                                        value={username}
                                                        onChange={(e) => setUsername(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-lg-6 mb-3">
                                                <div className="form-group">
                                                    <label className="mont-font fw-600 font-xsss mb-2">
                                                        Email
                                                    </label>
                                                    <input
                                                        id="usuario-email"
                                                        type="text"
                                                        className="form-control"
                                                        value={email}
                                                        onChange={(e) => setEmail(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-lg-6 mb-3">
                                                <div className="form-group">
                                                    <label className="mont-font fw-600 font-xsss mb-2">
                                                        Telefone
                                                    </label>
                                                    <input
                                                        id="usuario-telefone"
                                                        type="text"
                                                        className="form-control"
                                                        value={telefone}
                                                        onChange={(e) => setTelefone(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-lg-6 mb-3">
                                                <div className="form-group">
                                                    <label className="mont-font fw-600 font-xsss mb-2">
                                                        Gênero
                                                    </label>
                                                    <select
                                                        id="atleta-genero"
                                                        className="form-select form-control"
                                                        value={genero}
                                                        onChange={(e) => setGenero(e.target.value)}
                                                    >
                                                        <option value="">Selecione</option>
                                                        <option value="MASCULINO">Masculino</option>
                                                        <option value="FEMININO">Feminino</option>
                                                        <option value="OUTRO">Outro</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="col-lg-6 mb-3">
                                                <div className="form-group">
                                                    <label className="mont-font fw-600 font-xsss mb-2">
                                                        Tamanho da Camiseta
                                                    </label>
                                                    <select
                                                        id="atleta-tamanho-camiseta"
                                                        className="form-select form-control"
                                                        value={tamanhoCamiseta}
                                                        onChange={(e) => setTamanhoCamiseta(e.target.value)}
                                                    >
                                                        <option value="">Selecione</option>
                                                        <option value="PP">PP</option>
                                                        <option value="P">P</option>
                                                        <option value="M">M</option>
                                                        <option value="G">G</option>
                                                        <option value="GG">GG</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="col-lg-6 mb-3">
                                                <div className="form-group">
                                                    <label className="mont-font fw-600 font-xsss mb-2">
                                                        Tamanho do Sapato
                                                    </label>
                                                    <input
                                                        id="atleta-tamanho-sapato"
                                                        type="number"
                                                        className="form-control"
                                                        value={tamanhoSapato}
                                                        onChange={(e) => setTamanhoSapato(e.target.value === '' ? '' : Number(e.target.value))}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-lg-6 mb-3">
                                                <div className="form-group">
                                                    <label className="mont-font fw-600 font-xsss mb-2">
                                                        CPF
                                                    </label>
                                                    <p
                                                        id="atleta-cpf"
                                                        className="form-control bg-greylight mb-0"
                                                        style={{ lineHeight: "inherit" }}
                                                    >
                                                        {formatCpf(cpf)}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="col-lg-6 mb-3">
                                                <div className="form-group">
                                                    <label className="mont-font fw-600 font-xsss mb-2">
                                                        Data de Nascimento
                                                    </label>
                                                    <input
                                                        id="atleta-data-nascimento"
                                                        type="date"
                                                        className="form-control"
                                                        value={dataNascimento}
                                                        onChange={(e) => setDataNascimento(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-lg-6 mb-3">
                                                <div className="form-group">
                                                    <label className="mont-font fw-600 font-xsss mb-2">
                                                        Estado
                                                    </label>
                                                    <Autocomplete
                                                        id="atleta-estado"
                                                        options={estadosData?.estados || []}
                                                        getOptionLabel={(option: Estado) => option.nome}
                                                        value={estado}
                                                        onChange={(_, newValue) => {
                                                            setEstado(newValue);
                                                            setCidade(null);
                                                        }}
                                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                                        renderInput={(params) => (
                                                            <TextField {...params} variant="outlined" placeholder="Selecione o estado" />
                                                        )}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-lg-6 mb-3">
                                                <div className="form-group">
                                                    <label className="mont-font fw-600 font-xsss mb-2">
                                                        Cidade
                                                    </label>
                                                    <Autocomplete
                                                        id="atleta-cidade"
                                                        options={cidadesData?.cidades ? cidadesData.cidades.filter(cidade => cidade.estado.id === estado?.id) : []}
                                                        getOptionLabel={(option: Cidade) => option.nome}
                                                        value={cidade}
                                                        onChange={(_, newValue) => setCidade(newValue)}
                                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                                        renderInput={(params) => (
                                                            <TextField {...params} variant="outlined" placeholder="Selecione a cidade" />
                                                        )}
                                                        disabled={!estado}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-lg-12 mb-3">
                                                <div className="form-group">
                                                    <label className="mont-font fw-600 font-xsss mb-2">
                                                        Sobre
                                                    </label>
                                                   <textarea
                                                        id="atleta-sobre"
                                                        className="form-control mb-0 p-3 h100 bg-greylight lh-16"
                                                        value={sobre}
                                                        placeholder="Escreva algo sobre você..."
                                                        onChange={(e) => setSobre(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {message && (
                                            <div className="alert alert-success" role="alert">
                                                {message}
                                            </div>
                                        )}
                                        {erro && (
                                            <div className="alert alert-danger" role="alert">
                                                {erro}
                                            </div>
                                        )}

                                        <div className="row">
                                            <div className="col-lg-12 col-sm-12 d-flex justify-content-end">
                                                <button
                                                    type="submit"
                                                    disabled={saving || savingUser}
                                                    className="bg-current text-center text-white font-xsss fw-600 p-3 w175 rounded-3 d-inline-block"
                                                    style={{ border: "none" }}
                                                >
                                                    {saving || savingUser ? "Salvando..." : "Salvar"}
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <UpdateAvatarModal
                open={showAvatarModal}
                onClose={() => setShowAvatarModal(false)}
                currentImage={avatarUrl || undefined}
                onSave={handleSaveAvatar}
            />
        </Fragment>
    );
};

export default AccountInfo;
